// Deterministic schematic export — pure SVG serializer for the flow engine
// (wave-2 system map, G7). Ported approach: canvas_extras_preview's
// download_image fixture replays the live SVG edge paths through Path2D and
// paints nodes as rounded rect + label; this lib renders the same schematic
// to a standalone SVG STRING instead of a 2D context.
//
// Input is DATA ONLY — node/edge defs from flow_controller.toObject(),
// measured node boxes, replayed edge-path geometry and theme tokens, all
// gathered caller-side (ui--flow-export controller) from the live canvas.
// Nothing here touches the DOM, so identical input yields byte-identical
// output: the export is canonical by construction and viewer state (pan/zoom,
// selection, highlight) can never leak into it.
//
// Deliberately SCHEMATIC (download_image parity): nodes paint as rounded
// rect + label + type tint, edges replay their live path `d` with their
// resolved stroke. Rich node bodies, edge labels/markers, the background
// pattern and all chrome are NOT captured — the toolbar's "Export JSON"
// (toObject() → file) remains the lossless export.

// Card geometry is the archify share-card frame: a fixed 1200×630 canvas
// (the og:image size) with an outer pad, a title band on top and a subtitle
// band at the bottom; the map fits what remains.
export const EXPORT_PADDING = 24
export const CARD_WIDTH = 1200
export const CARD_HEIGHT = 630
export const CARD_PAD = 48
export const CARD_TITLE_BAND = 72
export const CARD_SUBTITLE_BAND = 44
export const CARD_TITLE_FONT_SIZE = 30
export const CARD_SUBTITLE_FONT_SIZE = 16

// Animated export (wave-3 W3-C): the recorded raster plays a scripted,
// fixed-duration camera ease over the fitted graph. WEBM_ZOOM is the final
// scale factor of the path (fit is scale 1).
export const WEBM_DURATION_MS = 4000
export const WEBM_FPS = 30
export const WEBM_ZOOM = 2.5

const NODE_RADIUS = 8
const NODE_STROKE_WIDTH = 1
const LABEL_DX = 12
const LABEL_FONT_SIZE = 11
const FONT_FAMILY = "system-ui, -apple-system, 'Segoe UI', sans-serif"

// Stable number rendering — 2-decimal rounding with -0 normalized — so the
// serialized string is byte-identical for identical input across platforms
// (the unit pins assert exact substrings).
function num(value) {
  const n = Math.round((Number(value) + Number.EPSILON) * 100) / 100
  return String(Object.is(n, -0) ? 0 : n)
}

function esc(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
}

// Flow-space bounds over the EXPORTED nodes only (hidden defs and defs with
// no measured box paint nothing, so they must not stretch the frame either).
// Null when nothing paints — callers render the explicit empty state.
export function contentBounds(nodes, nodeBoxes) {
  let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity
  for (const def of nodes || []) {
    if (def.hidden) continue
    const box = nodeBoxes?.[def.id]
    if (!box || !(box.width > 0) || !(box.height > 0)) continue
    x1 = Math.min(x1, box.x)
    y1 = Math.min(y1, box.y)
    x2 = Math.max(x2, box.x + box.width)
    y2 = Math.max(y2, box.y + box.height)
  }
  if (x1 === Infinity) return null
  return { x: x1, y: y1, width: x2 - x1, height: y2 - y1 }
}

// Edges paint first (under the node cards), iterating the AUTHORED edge
// array — never the DOM — so ordering is deterministic by construction. An
// edge whose live path geometry was not captured (hidden, or never laid out)
// is skipped: schematic export shows what the canvas shows, nothing more.
function edgeElements(edges, edgePaths) {
  let out = ""
  for (const def of edges || []) {
    if (def.hidden) continue
    const geo = edgePaths?.[def.id]
    if (!geo || !geo.d) continue
    out += `<path d="${esc(geo.d)}" fill="none" stroke="${esc(geo.stroke)}" stroke-width="${num(geo.strokeWidth ?? 1.5)}"/>`
  }
  return out
}

// One card per node: rounded rect (fill = resolved card background, stroke =
// the type tint resolved from the live element's border-color — TYPE_CHROME
// utilities carry the type colors, so no hardcoded type→hex map exists here)
// plus the def label, vertically centered like the download_image fixture.
function nodeElements(nodes, nodeBoxes, nodeStyles) {
  let out = ""
  for (const def of nodes || []) {
    if (def.hidden) continue
    const box = nodeBoxes?.[def.id]
    if (!box || !(box.width > 0) || !(box.height > 0)) continue
    const style = nodeStyles?.[def.id] || {}
    const label = def.label ?? def.data?.label ?? def.id
    out += `<rect x="${num(box.x)}" y="${num(box.y)}" width="${num(box.width)}" height="${num(box.height)}" rx="${NODE_RADIUS}" fill="${esc(style.background || "none")}" stroke="${esc(style.tint || "none")}" stroke-width="${NODE_STROKE_WIDTH}"/>` +
      `<text x="${num(box.x + LABEL_DX)}" y="${num(box.y + box.height / 2)}" font-family="${FONT_FAMILY}" font-size="${LABEL_FONT_SIZE}" fill="${esc(style.textColor || "none")}" dominant-baseline="central">${esc(label)}</text>`
  }
  return out
}

function flowContent(input) {
  return edgeElements(input.edges, input.edgePaths) +
    nodeElements(input.nodes, input.nodeBoxes, input.nodeStyles)
}

// Frame shared by serializeFlowSvg/flowSvgSize: viewBox 0 0 W H with the
// graph translated by (dx, dy) so bounds + padding land inside it. The
// content group's translate IS the export's viewport transform — dropping it
// shifts the whole graph out of frame (the misalignment mutation pin).
function frame(nodes, nodeBoxes, padding) {
  const bounds = contentBounds(nodes, nodeBoxes)
  const width = (bounds ? bounds.width : 0) + padding * 2
  const height = (bounds ? bounds.height : 0) + padding * 2
  return {
    bounds,
    width,
    height,
    dx: bounds ? padding - bounds.x : padding,
    dy: bounds ? padding - bounds.y : padding
  }
}

// Pixel size of the standalone SVG — the controller needs it to size the
// raster canvas for PNG export without parsing the string back.
export function flowSvgSize({ nodes, nodeBoxes = {}, padding = EXPORT_PADDING } = {}) {
  const { width, height } = frame(nodes, nodeBoxes, padding)
  return { width, height }
}

// Standalone schematic SVG: full graph fitted to its content bounds + pad
// (NOT the current viewport — pan/zoom are viewer state and never read).
// Empty graph → a padding-sized background-only SVG, deterministic as well.
// backgroundOpacity (alpha-split token, wave-3 NIT) paints the bg rect with
// a proper fill-opacity attribute instead of an invalid raw channel string.
export function serializeFlowSvg({
  nodes, edges, nodeBoxes = {}, nodeStyles = {}, edgePaths = {},
  background, backgroundOpacity, title, padding = EXPORT_PADDING
} = {}) {
  const { bounds, width, height, dx, dy } = frame(nodes, nodeBoxes, padding)
  const titleEl = title ? `<title>${esc(title)}</title>` : ""
  const bgOp = backgroundOpacity == null ? "" : ` fill-opacity="${num(backgroundOpacity)}"`
  const bg = `<rect width="100%" height="100%" fill="${esc(background || "none")}"${bgOp}/>`
  const content = bounds
    ? `<g transform="translate(${num(dx)} ${num(dy)})">${flowContent({ nodes, edges, nodeBoxes, nodeStyles, edgePaths })}</g>`
    : ""
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${num(width)} ${num(height)}" width="${num(width)}" height="${num(height)}" role="img">${titleEl}${bg}${content}</svg>`
}

// Share card — the archify card geometry: 1200×630, title band on top,
// subtitle at the bottom (host copy, defaulting to the page's meta
// description), and the same schematic map scaled to fit between them. The
// pad feeds the title/subtitle positions AND the map frame, so dropping it
// shifts every number the geometry pin asserts.
export function serializeShareCard({
  nodes, edges, nodeBoxes = {}, nodeStyles = {}, edgePaths = {},
  background, backgroundOpacity, foreground, foregroundOpacity, mutedForeground, mutedForegroundOpacity,
  title, subtitle,
  width = CARD_WIDTH, height = CARD_HEIGHT, pad = CARD_PAD,
  titleBand = CARD_TITLE_BAND, subtitleBand = CARD_SUBTITLE_BAND
} = {}) {
  const opacityAttr = (name, value) => (value == null ? "" : ` ${name}="${num(value)}"`)
  const bg = `<rect width="100%" height="100%" fill="${esc(background || "none")}"${opacityAttr("fill-opacity", backgroundOpacity)}/>`
  const titleEl = title
    ? `<text x="${num(pad)}" y="${num(pad + CARD_TITLE_FONT_SIZE)}" font-family="${FONT_FAMILY}" font-size="${CARD_TITLE_FONT_SIZE}" font-weight="600" fill="${esc(foreground || "none")}"${opacityAttr("fill-opacity", foregroundOpacity)}>${esc(title)}</text>`
    : ""
  const subtitleEl = subtitle
    ? `<text x="${num(pad)}" y="${num(height - pad)}" font-family="${FONT_FAMILY}" font-size="${CARD_SUBTITLE_FONT_SIZE}" fill="${esc(mutedForeground || "none")}"${opacityAttr("fill-opacity", mutedForegroundOpacity)}>${esc(subtitle)}</text>`
    : ""
  let map = ""
  const bounds = contentBounds(nodes, nodeBoxes)
  if (bounds) {
    const mapX = pad
    const mapY = pad + titleBand
    const mapW = width - pad * 2
    const mapH = height - pad - subtitleBand - mapY
    const scale = Math.min(mapW / bounds.width, mapH / bounds.height)
    const tx = mapX + (mapW - bounds.width * scale) / 2 - bounds.x * scale
    const ty = mapY + (mapH - bounds.height * scale) / 2 - bounds.y * scale
    map = `<g transform="translate(${num(tx)} ${num(ty)}) scale(${num(scale)})">${flowContent({ nodes, edges, nodeBoxes, nodeStyles, edgePaths })}</g>`
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${num(width)} ${num(height)}" width="${num(width)}" height="${num(height)}" role="img">${bg}${map}${titleEl}${subtitleEl}</svg>`
}

// ── Dual-theme variant (wave-3 W3-C) ───────────────────────────────────
// ONE standalone SVG carrying the light and the dark paint as two sibling
// groups with IDENTICAL geometry: the frame is computed once (from the
// light snapshot's boxes) and each variant group repaints the same content
// with its own resolved tokens. An embedded <style> flips visibility on
// @media (prefers-color-scheme) — no scripting, the file stays a pure
// image, and same input + same token maps → same bytes. Class names are
// prefixed so host CSS can never collide with the switch.
//
// Geometry contract (W3-C recheck F-2): "identical geometry" is frame-EXACT
// (computed once) and paint-only for the content — each variant paints from
// its OWN snapshot's nodeBoxes. That holds exactly for token-only themes
// (paint changes, layout never); a host theme that changed LAYOUT (not just
// paint) between variants would diverge the two groups' content geometry
// while the shared frame stays. The translate-identity pin asserts the
// frame half of this contract.
const DUAL_STYLE =
  `<style>.dt-du--dark{display:none} @media (prefers-color-scheme: dark){.dt-du--light{display:none}.dt-du--dark{display:inline}}</style>`

export function serializeDualThemeFlowSvg({ light, dark, title, padding = EXPORT_PADDING } = {}) {
  const { bounds, width, height, dx, dy } = frame(light?.nodes, light?.nodeBoxes, padding)
  const titleEl = title ? `<title>${esc(title)}</title>` : ""
  const variant = (snap, cls) => {
    const bgOp = snap?.backgroundOpacity == null ? "" : ` fill-opacity="${num(snap.backgroundOpacity)}"`
    const bg = `<rect width="100%" height="100%" fill="${esc(snap?.background || "none")}"${bgOp}/>`
    const content = bounds
      ? `<g transform="translate(${num(dx)} ${num(dy)})">${flowContent({ nodes: snap?.nodes, edges: snap?.edges, nodeBoxes: snap?.nodeBoxes, nodeStyles: snap?.nodeStyles, edgePaths: snap?.edgePaths })}</g>`
      : ""
    return `<g class="${cls}">${bg}${content}</g>`
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${num(width)} ${num(height)}" width="${num(width)}" height="${num(height)}" role="img">${titleEl}${DUAL_STYLE}${variant(light, "dt-du--light")}${variant(dark, "dt-du--dark")}</svg>`
}

// ── Render-quality gates (wave-3 W3-C) ─────────────────────────────────
// Pure validators for the export artifacts (not graph semantics): the gate
// reads ONLY the serialized string plus the same input snapshot the
// serializer received, so a PASS means the artifact is renderable exactly
// as produced and a FAIL lists every broken invariant. Checks: corrupted
// coordinate literals (NaN) and leaked placeholders (undefined); raw
// var(--…) references or bare token channels (tokens left unresolved);
// every visible node's label present; every painted edge path carrying a
// non-empty d and every captured live path present; the viewBox fitting
// the measured bounds + pad.
const CORRUPT_LITERALS = ["NaN", "undefined"]
const TOKEN_REFERENCE = /var\(--/
// A fill/stroke starting with a digit can only be unresolved HSL token
// channels ("183 55% 50%" — valid colors begin with # or a letter).
const BARE_CHANNELS = /(?:fill|stroke)="\d/

function corruptReasons(svg, reasons) {
  for (const bad of CORRUPT_LITERALS) {
    if (svg.includes(bad)) reasons.push(`corrupted literal "${bad}" in the output`)
  }
  if (TOKEN_REFERENCE.test(svg)) reasons.push("unresolved token reference var(--…) leaked into the output")
  if (BARE_CHANNELS.test(svg)) reasons.push("unresolved token channels in a fill/stroke attribute")
}

// Node labels render ONLY inside the map group (the flow svg's content
// group / the card's map group both start with `<g transform="translate`),
// while the card's title/subtitle bands render as <text> OUTSIDE it and the
// flow svg's <title> closes with </title> — scoping to the translate group
// makes per-label occurrence counting exact.
function mapRegion(svg) {
  const start = svg.indexOf('<g transform="translate')
  if (start === -1) return ""
  return svg.slice(start, svg.lastIndexOf("</g>"))
}

function labelReasons(svg, nodes, nodeBoxes, reasons) {
  // F-1 (recheck): occurrence-COUNTED presence, not a value-presence
  // includes() — two same-label nodes shared ONE surviving <text> and the
  // gate still passed (live-reproduced). Each visible+boxed def needs its
  // own <text> element in the map region; a selective drop now fails.
  const need = new Map() // escaped label → defs expecting it
  for (const def of nodes || []) {
    if (def.hidden) continue
    const box = nodeBoxes?.[def.id]
    if (!box || !(box.width > 0) || !(box.height > 0)) continue // paints nothing, asserts nothing
    const label = esc(def.label ?? def.data?.label ?? def.id)
    if (!need.has(label)) need.set(label, [])
    need.get(label).push(def)
  }
  const region = mapRegion(svg)
  for (const [label, defs] of need) {
    const got = region.split(`>${label}</text>`).length - 1
    if (got < defs.length) {
      reasons.push(defs.length === 1
        ? `node "${defs[0].id}" label missing from the output`
        : `label "${label}" painted ${got}× but ${defs.length} visible nodes expect it (${defs.map(d => `"${d.id}"`).join(", ")}) — a node label is missing from the output`)
    }
  }
}

function pathReasons(svg, edges, edgePaths, reasons) {
  for (const tag of svg.match(/<path\b[^>]*>/g) || []) {
    if (!/\bd="[^"]+"/.test(tag)) reasons.push("an edge path carries an empty d attribute")
  }
  // F-1 (recheck): per-d occurrence counting — two edges may share an
  // identical d; one surviving path must not satisfy both defs.
  const need = new Map() // escaped d → defs expecting it
  for (const def of edges || []) {
    if (def.hidden) continue
    const geo = edgePaths?.[def.id]
    if (!geo || !geo.d) continue // uncaptured geometry paints nothing (serializer rule)
    const d = esc(geo.d)
    if (!need.has(d)) need.set(d, [])
    need.get(d).push(def)
  }
  for (const [d, defs] of need) {
    const got = svg.split(`d="${d}"`).length - 1
    if (got < defs.length) {
      reasons.push(defs.length === 1
        ? `edge "${defs[0].id}" live path missing from the output`
        : `a captured edge path appears ${got}× but ${defs.length} edges expect it (${defs.map(e => `"${e.id}"`).join(", ")}) — an edge path is missing from the output`)
    }
  }
}

// viewBox must be exactly the measured content bounds + pad (both rounded
// with the serializer's own num()), proved against the same snapshot.
function viewBoxReasons(svg, nodes, nodeBoxes, padding, reasons) {
  const m = svg.match(/viewBox="0 0 ([\d.]+) ([\d.]+)"/)
  if (!m) { reasons.push("viewBox missing or unparsable"); return }
  const pad = padding ?? EXPORT_PADDING
  const bounds = contentBounds(nodes, nodeBoxes)
  const width = num((bounds ? bounds.width : 0) + pad * 2)
  const height = num((bounds ? bounds.height : 0) + pad * 2)
  if (m[1] !== width || m[2] !== height) {
    reasons.push(`viewBox ${m[1]}×${m[2]} does not fit the measured bounds + pad (expected ${width}×${height})`)
  }
}

export function validateFlowSvg(svg, expected = {}) {
  const reasons = []
  if (typeof svg !== "string" || !svg.startsWith("<svg")) {
    return { ok: false, reasons: ["not an SVG string"] }
  }
  corruptReasons(svg, reasons)
  labelReasons(svg, expected.nodes, expected.nodeBoxes, reasons)
  pathReasons(svg, expected.edges, expected.edgePaths, reasons)
  viewBoxReasons(svg, expected.nodes, expected.nodeBoxes, expected.padding, reasons)
  return { ok: reasons.length === 0, reasons }
}

export function validateShareCard(card, expected = {}) {
  const reasons = []
  if (typeof card !== "string" || !card.startsWith("<svg")) {
    return { ok: false, reasons: ["not an SVG string"] }
  }
  corruptReasons(card, reasons)
  labelReasons(card, expected.nodes, expected.nodeBoxes, reasons)
  pathReasons(card, expected.edges, expected.edgePaths, reasons)
  const width = expected.width ?? CARD_WIDTH
  const height = expected.height ?? CARD_HEIGHT
  if (!card.includes(`viewBox="0 0 ${num(width)} ${num(height)}"`)) {
    reasons.push(`card frame is not the ${num(width)}×${num(height)} share geometry`)
  }
  if (expected.title && !card.includes(`>${esc(expected.title)}<`)) reasons.push("title missing from the card")
  if (expected.subtitle && !card.includes(`>${esc(expected.subtitle)}<`)) reasons.push("subtitle missing from the card")
  const hasVisibleMap = (expected.nodes || []).some((def) =>
    !def.hidden && expected.nodeBoxes?.[def.id]?.width > 0 && expected.nodeBoxes?.[def.id]?.height > 0)
  if (hasVisibleMap && !card.includes("<g transform=")) reasons.push("map group missing from the card")
  return { ok: reasons.length === 0, reasons }
}

// ── Animated export camera (wave-3 W3-C) ───────────────────────────────
// Scripted deterministic camera: pure frame math only — the controller
// supplies the endpoints (from the snapshot) and drives the frames, so
// given the same t the drawn frame is always the same.
//
// inOutCubic over clamped [0,1]; scale + focus point interpolate together.
export function cameraPath(t, from, to) {
  const c = Math.max(0, Math.min(1, Number(t) || 0))
  const k = c < 0.5 ? 4 * c * c * c : 1 - Math.pow(-2 * c + 2, 3) / 2
  return {
    scale: from.scale + (to.scale - from.scale) * k,
    cx: from.cx + (to.cx - from.cx) * k,
    cy: from.cy + (to.cy - from.cy) * k
  }
}

// Canvas-pixel endpoints for the recorded raster: fit = identity at the
// canvas center (the export is already fit-by-construction), zoom =
// WEBM_ZOOM onto the focus point the controller picked (the first visible
// node's center — "from the fitted whole onto the map's starting stage").
export function cameraEndpoints({ width, height, focusX, focusY, zoom = WEBM_ZOOM } = {}) {
  return {
    from: { scale: 1, cx: width / 2, cy: height / 2 },
    to: { scale: zoom, cx: focusX ?? width / 2, cy: focusY ?? height / 2 }
  }
}
